import React, { memo, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ColorConstants from 'constants/ColorConstants';
import { classifyDiffSegment } from './codeDiffUtils';
import { buildHeatmapCells } from './heatmapBins';

// Minimap-style agreement heatmap that sits in the gutter between the two
// comparison columns: the strip represents the whole visible transcript
// compressed to viewport height, with a block wherever there's coded text,
// positioned at that text's actual vertical spot — a line highlighted as
// agreement in the transcript lines up with the agreement color at that same
// height in the strip. The strip itself is sticky, a translucent window shows
// the part of the transcript currently on screen, and clicking anywhere on
// the strip scrolls the transcript to that spot. Cell positions are measured
// off the real paragraph DOM nodes so blocks line up with where the text
// actually is.
//
// Palette: agreement colors in green/red, validated with the dataviz
// palette checker (all-pairs CVD separation under
// protanopia/deuteranopia/tritanopia; worst pair — agree green vs conflict
// red under deuteranopia — ΔE 9.7, target ≥ 8). The agree green is
// deliberately a BLUISH green (Okabe-Ito) because that red against a pure
// green collapses under deuteranopia (ΔE 4.5, far below the floor); the
// blue cast keeps the pair separable while still reading as "green =
// agreement". Width steps and tooltips (below) carry the meaning through
// any residual ambiguity, so hue never stands alone.
export const AGREE_CELL = '#009e73';
export const CONFLICT_CELL = '#c0392b';
// Partial agreement wears no third hue (amber is dropped from the page
// entirely, per Alex; a green/red candy-stripe cell was tried and rejected
// too): it's a milder form of disagreement, so it sits on the conflict red's
// own axis as a clearly LIGHTER red — same hue family says "something's off
// here," the lightness step says "less severe than full conflict," and
// lightness is a CVD-safe channel, unlike a third hue.
export const PARTIAL_CELL = '#d98880';
// One-sided coverage (coded by only one coder) is deliberately OUTSIDE the
// agreement palette: it's coverage info, not a verdict, so it renders as a
// faint neutral gray — visible against the #eee track but quiet next to the
// saturated agreement colors, and separable from all three by lightness
// alone, so it's CVD-safe by construction.
export const ONE_SIDED_CELL = ColorConstants.GRAY_300;

export const CELL_COLORS = {
  agree: AGREE_CELL,
  partial: PARTIAL_CELL,
  conflict: CONFLICT_CELL,
  oneSided: ONE_SIDED_CELL,
};

// Cells are uniform track width — the strip reads as one slim minimap line
// (the approved proto look). Non-hue meaning is carried by the tooltip
// labels below and by the palette's validated lightness separation, not by
// width steps, which made the strip read as a chunky bar.
const CELL_WIDTHS = {
  agree: 6,
  partial: 6,
  conflict: 6,
  oneSided: 6,
};

// The native-tooltip text channel (so no cell's meaning is carried by color
// alone) is produced per bin by heatmapBins.describeRun, including the mix
// when a run isn't pure.

const TRACK_WIDTH = 6;
const STRIP_HIT_WIDTH = 14; // widest cell + a little slack; also the click target

// Cells are fixed-height BINS, not one cell per coded segment (see
// heatmapBins.js): each bin sums the coded text of each agreement state that
// falls into it and resolves to one state by share × priority.
// On a regular transcript a segment spans many bins that merge back into one
// flush band, so mixed paragraphs still show both colors at the right
// positions; on a dense transcript cells can no longer pile up and hide each
// other. Cells are rendered flush (no gap, no per-cell radius) so adjacent
// same-state bins read as clean bands rather than a fragmented stack of pills.
//
// `scrollbarsRef` is a ref to the react-custom-scrollbars <Scrollbars>
// instance wrapping this page's content — `.view` is the real scrollable DOM
// node underneath its custom track/thumb UI, which is what we need to
// measure and listen to scroll on.

// The color cells as one memoized layer of plain divs: the viewport window
// above them re-renders on every scroll frame (setState per rAF), and
// re-rendering hundreds of styled MUI Boxes per frame was measured at
// ~23ms/frame scroll jank on a 400-paragraph page. Plain divs + memo keep
// the per-frame work to just the window div.
const HeatmapCells = memo(({ cells }) => (
  <>
    {cells.map((cell) => (
      <div
        key={cell.key}
        title={cell.label}
        data-testid="heatmap-cell"
        data-agreement={cell.classification}
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          width: `${cell.width}px`,
          top: `${cell.top}px`,
          height: `${cell.height}px`,
          backgroundColor: cell.color,
        }}
      />
    ))}
  </>
));

const ComparisonHeatmapStrip = ({ paragraphs, diffsByParagraph, paragraphRefs, scrollbarsRef, fillViewport = false }) => {
  const wrapperRef = useRef(null);
  const stripRef = useRef(null);
  const [cells, setCells] = useState([]);
  const [viewport, setViewport] = useState({ top: 0, height: 0 });
  const [stripHeight, setStripHeight] = useState(0);
  const [stickyTop, setStickyTop] = useState(0);

  const getContainer = () => scrollbarsRef.current?.view || null;

  // The strip sits below the page's header (title, user dropdowns, etc.), so
  // its natural top offset — measured before any scrolling has happened — is
  // however tall that header is. Using that same offset as both the sticky
  // `top` and the height budget means the strip's bottom edge lines up with
  // the bottom of the visible area at every scroll position (stuck or not),
  // instead of running taller than the viewport and needing its own scroll
  // to see the rest of it.
  //
  // The height is ALSO capped at the rail's height (the absolute wrapper
  // ComparisonView mounts this in, which spans the paragraph grid) UNLESS
  // `fillViewport` is set: on a single-page transcript shorter than the
  // viewport the strip then runs the full pane height like an editor
  // minimap — cells still sit at the text's real positions near the top,
  // and the empty track below is expected. The gutter cell contributes no
  // height to the grid, so the rows don't stretch to meet it. ComparisonView
  // only sets fillViewport when no pager renders below the grid; with a
  // pager the strip stays capped, or its overflow would draw over the pager
  // controls. Re-measured when the page of paragraphs changes, since that
  // changes the rail height.
  useEffect(() => {
    const updateLayout = () => {
      const container = getContainer();
      const wrapper = wrapperRef.current;
      if (!container || !wrapper) return;
      const containerRect = container.getBoundingClientRect();
      // Measure the RAIL (the absolute wrapper this strip rides in), not the
      // sticky wrapper itself, and add scrollTop to convert to a content
      // offset: the sticky wrapper's viewport offset only equals the header
      // height at scrollTop 0, so re-measuring while scrolled (resize, or
      // the settle pass after an early scroll) ratcheted stickyTop down and
      // the strip taller than the viewport.
      const rail = wrapper.parentElement || wrapper;
      const railRect = rail.getBoundingClientRect();
      const top = Math.max(0, railRect.top - containerRect.top + container.scrollTop);
      const viewportBudget = Math.max(120, containerRect.height - top - 16);
      const railHeight = fillViewport ? Infinity : (railRect.height || Infinity);
      setStickyTop(top);
      setStripHeight(Math.max(24, Math.min(viewportBudget, railHeight)));
    };
    updateLayout();
    const settle = setTimeout(updateLayout, 600);
    window.addEventListener('resize', updateLayout);
    return () => {
      clearTimeout(settle);
      window.removeEventListener('resize', updateLayout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paragraphs, fillViewport]);

  useEffect(() => {
    const measure = () => {
      const container = getContainer();
      const strip = stripRef.current;
      if (!container || !strip) return;
      const stripH = strip.clientHeight;
      const scrollH = container.scrollHeight;
      const containerTop = container.getBoundingClientRect().top;
      const segmentsInStrip = [];
      paragraphs.forEach((paragraph, i) => {
        const el = paragraphRefs.current[i];
        const diff = diffsByParagraph[i];
        if (!el || !diff) return;

        // Each cell is placed at its segment's actual character offset
        // within the paragraph's RENDERED BODY TEXT — so a line highlighted
        // in the transcript lines up with a cell at that same vertical
        // position in the strip, not just "somewhere in this paragraph's
        // block." The row element also contains the speaker header above
        // the text, the chip list below it, and cross-column grid stretch,
        // none of which correspond to character offsets — so measure the
        // extent of the text spans themselves (every piece of body text
        // renders as a .codeSpan) and fall back to the row only if none
        // exist. Cells anchor to the LEFT column's text (these refs), the
        // same anchor the click-to-scroll lands on.
        const rowRect = el.getBoundingClientRect();
        const spans = el.getElementsByClassName('codeSpan');
        let textTop = rowRect.top;
        let textBottom = rowRect.top + el.offsetHeight;
        if (spans.length) {
          textTop = spans[0].getBoundingClientRect().top;
          textBottom = spans[spans.length - 1].getBoundingClientRect().bottom;
        }
        const elTop = textTop - containerTop + container.scrollTop;
        const elHeight = Math.max(1, textBottom - textTop);
        const bodyLen = ((paragraph.bodyText || '').length) || 1;
        diff.segments.forEach((segment, j) => {
          const classification = classifyDiffSegment(segment);
          if (!classification) return;
          const segTop = elTop + (segment.start / bodyLen) * elHeight;
          const segHeight = ((segment.end - segment.start) / bodyLen) * elHeight;
          segmentsInStrip.push({
            classification,
            top: (segTop / scrollH) * stripH,
            height: (segHeight / scrollH) * stripH,
          });
        });
      });
      setCells(
        buildHeatmapCells(segmentsInStrip, stripH).map((cell) => ({
          ...cell,
          color: CELL_COLORS[cell.classification],
          width: CELL_WIDTHS[cell.classification],
        }))
      );
    };
    measure();
    // Second pass after fonts/layout settle so measured offsets are right.
    const settle = setTimeout(measure, 600);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(settle);
      window.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paragraphs, diffsByParagraph, stripHeight]);

  // The "you are here" window. Re-run (not just on scroll/resize events)
  // whenever the strip's height lands or the page of paragraphs changes:
  // the initial pass otherwise scales against the pre-measure fallback
  // height, and paging or codings changing row heights alters scrollHeight
  // with no scroll event — either leaves the window mis-sized until the
  // user happens to scroll.
  useEffect(() => {
    const container = getContainer();
    const strip = stripRef.current;
    if (!container || !strip) return undefined;
    const update = () => {
      const stripH = strip.clientHeight;
      setViewport({
        top: (container.scrollTop / container.scrollHeight) * stripH,
        height: (container.clientHeight / container.scrollHeight) * stripH,
      });
    };
    update();
    // Settle pass mirrors the cell measurement's: fonts/layout can shift
    // scrollHeight after first paint.
    const settle = setTimeout(update, 650);
    // rAF-coalesced: scroll events can outpace frames, and each update is a
    // setState — once per frame is all the window can show anyway.
    let rafId = null;
    const scheduleUpdate = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };
    container.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      clearTimeout(settle);
      if (rafId != null) cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripHeight, paragraphs, diffsByParagraph]);

  const handleClick = (e) => {
    const container = getContainer();
    const strip = stripRef.current;
    if (!container || !strip) return;
    const rect = strip.getBoundingClientRect();
    const fraction = (e.clientY - rect.top) / rect.height;
    container.scrollTo({
      top: fraction * container.scrollHeight - container.clientHeight / 2,
      behavior: 'smooth',
    });
  };

  return (
    <Box ref={wrapperRef} sx={{ position: 'sticky', top: `${stickyTop}px`, height: stripHeight ? `${stripHeight}px` : '80vh' }}>
      <Box
        ref={stripRef}
        onClick={handleClick}
        data-testid="comparison-heatmap-strip"
        sx={{
          position: 'relative',
          width: `${STRIP_HIT_WIDTH}px`,
          height: '100%',
          mx: 'auto',
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: `${TRACK_WIDTH}px`,
            backgroundColor: '#eee',
            borderRadius: '3px',
          }}
        />
        {/* Cells are flush (no vertical inset, no gap, no per-cell radius)
            so adjacent segments of the same state read as one continuous
            band instead of a stack of separate pills. Widths step with
            severity (the redundant non-hue cue), centered on the track so
            wider states protrude symmetrically. */}
        <HeatmapCells cells={cells} />
        {/* "You are here": a translucent block spanning the exact range of
            the transcript currently on screen, so it reads as a window onto
            the strip rather than just a point marker — it grows and shrinks
            with the viewport, sitting on top of the color cells without
            hiding them. It spans the full hit width, so its edges stay
            visible beyond even the widest cells. */}
        <div
          data-testid="heatmap-viewport-window"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${viewport.top}px`,
            height: `${viewport.height}px`,
            backgroundColor: 'rgba(53, 104, 201, 0.15)',
            borderRadius: '3px',
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
  );
};

export default ComparisonHeatmapStrip;
